import { groq } from "next-sanity";
import { handleClientError } from "../../errors";
import { getClient } from "../sanity";
import { type Customer, customerFragment } from "./customers";
import { type IntegrationSimple, integrationSimpleFragment } from "./integrations";

export type AbmRelatedContent = {
  type: "caseStudy" | "resource" | "playbook";
  slug: string;
  title: string;
  subtitle?: string;
  resourceImageUrl?: string;
  playbookImageUrl?: string;
  customerSlug?: string;
  customerLogo?: string;
  customerName?: string;
};

export type AbmIndustry = {
  slug: string;
  name: string;
  logos: Customer[];
  relatedContent: AbmRelatedContent[];
};

export type AbmPage = {
  slug: string;
  name: string;
  logoUrl?: string;
  industry: AbmIndustry;
  source: IntegrationSimple;
  destinations: IntegrationSimple[];
  audiences: string[];
  metaTitle?: string;
  metaDescription?: string;
};

export const getAbmPage = async (slug: string): Promise<AbmPage | undefined> => {
  const query = groq`
    *[_type == "abmPage" && slug == $slug] {
      slug,
      name,
      metaTitle,
      metaDescription,
      'logoUrl': logo->asset.url,
      audiences,
      source->{ ${integrationSimpleFragment} },
      destinations[]->{ ${integrationSimpleFragment} },
      industry->{
        slug,
        name,
        logos[]->{ ${customerFragment} },
        relatedContent[]->{
          'type': _type,
          slug,
          title,
          subtitle,
          'resourceImageUrl': cardImage.asset->url,
          'playbookImageUrl': image.asset->url,
          'customerSlug': customer->slug,
          'customerLogo': customer->logo.asset->url,
          'customerName': customer->name,
        }
      }
    }
  `;

  try {
    const parameters = { slug };
    const abmPage = await getClient().fetch(query, parameters);
    return abmPage[0];
  } catch (error) {
    const message = `Failed to fetch ABM page with slug "${slug}": ${
      error instanceof Error ? error.message : String(error)
    }`;
    handleClientError(message);
    return undefined;
  }
};
